import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { XIcon } from './icons';

interface ToastItem {
  id: number;
  mensaje: string;
  tipo: 'error' | 'exito';
}

interface ToastContextValue {
  mostrarError: (mensaje: string) => void;
  mostrarExito: (mensaje: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** FR-ERR-01: muestra el mensaje de error devuelto por el backend, sin reinterpretarlo. */
export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const agregar = useCallback((mensaje: string, tipo: ToastItem['tipo']) => {
    const id = Date.now() + Math.random();
    setToasts((actuales) => [...actuales, { id, mensaje, tipo }]);
    setTimeout(() => setToasts((actuales) => actuales.filter((t) => t.id !== id)), 5000);
  }, []);

  const value: ToastContextValue = {
    mostrarError: (mensaje) => agregar(mensaje, 'error'),
    mostrarExito: (mensaje) => agregar(mensaje, 'exito'),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2" data-testid="toast-container">
        {toasts.map((t) => (
          <div
            key={t.id}
            data-testid={`toast-${t.tipo}`}
            className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
              t.tipo === 'error' ? 'bg-danger text-white' : 'bg-success text-white'
            }`}
          >
            <span>{t.mensaje}</span>
            <button
              onClick={() => setToasts((actuales) => actuales.filter((x) => x.id !== t.id))}
              aria-label="Cerrar notificación"
            >
              <XIcon width={14} height={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return ctx;
}
